import React, { useState } from 'react';
import { motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { cars } from "../../services/api";

const CustomInput = React.forwardRef(({ value, onClick, placeholder, isError }, ref) => (
  <motion.button
    type="button"
    style={{ 
      border: isError ? "1px solid #ef4444" : "1px solid gray", 
      color: isError ? "#ef4444" : "gray" 
    }}
    className={`w-full h-[38px] border p-1.5 rounded text-left z-50 hover:border-red-500 transition-colors duration-300 ${
      isError ? 'bg-red-50' : 'bg-gray-100'
    }`}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    ref={ref}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    {value || placeholder}
  </motion.button>
));

const QuickSearch = ({ initialDates, onSearch }) => {
  const [dates, setDates] = useState({
    pickup: initialDates.pickup ? new Date(initialDates.pickup) : null,
    dropoff: initialDates.dropoff ? new Date(initialDates.dropoff) : null
  });
  const [dateErrors, setDateErrors] = useState({
    pickup: false,
    dropoff: false
  });
  const [datePickerOpen, setDatePickerOpen] = useState({
    pickup: false,
    dropoff: false
  });

  const validateDates = (newDate, type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (type === 'pickup') {
      // If drop-off is already selected, pick-up must be before or equal to drop-off
      if (dates.dropoff && newDate > dates.dropoff) {
        setDateErrors(prev => ({ ...prev, pickup: true }));
        return false;
      }
      // Pick-up date must be today or later
      if (newDate < today) {
        setDateErrors(prev => ({ ...prev, pickup: true }));
        return false;
      }
    } else if (type === 'dropoff') {
      // If pick-up is already selected, drop-off must be after or equal to pick-up
      if (dates.pickup && newDate < dates.pickup) {
        setDateErrors(prev => ({ ...prev, dropoff: true }));
        return false;
      }
      // Drop-off date must be today or later
      if (newDate < today) {
        setDateErrors(prev => ({ ...prev, dropoff: true }));
        return false;
      }
    }
    
    setDateErrors(prev => ({ ...prev, [type]: false }));
    return true;
  };

  const handleDateChange = (date, type) => {
    if (validateDates(date, type)) {
      setDates(prev => ({
        ...prev,
        [type]: date
      }));
    }
    setDatePickerOpen(prev => ({
      ...prev,
      [type]: false
    }));
  };
  const [isSearching, setIsSearching] = useState(false);
  
  const handleSearch = async () => {
    // Reset any previous errors
    setDateErrors({ pickup: false, dropoff: false });
    
    // Validate both dates are selected
    if (!dates.pickup || !dates.dropoff) {
      if (!dates.pickup) setDateErrors(prev => ({ ...prev, pickup: true }));
      if (!dates.dropoff) setDateErrors(prev => ({ ...prev, dropoff: true }));
      return;
    }

    // Final validation check
    const isPickUpValid = validateDates(dates.pickup, 'pickup');
    const isDropOffValid = validateDates(dates.dropoff, 'dropoff');

    if (!isPickUpValid || !isDropOffValid) {
      return;
    }

    setIsSearching(true);

    try {
      // Fetch dynamic pricing data for the selected date range
      const availabilityResponse = await cars.getAvailable({
        startDate: dates.pickup.toISOString(),
        endDate: dates.dropoff.toISOString(),
        includeAll: true
      });

      // Create a map of car IDs to dynamic prices
      const dynamicPriceMap = {};
      if (availabilityResponse.data && availabilityResponse.data.length) {
        availabilityResponse.data.forEach(car => {
          dynamicPriceMap[car._id] = car.dynamicPrice;
        });
      }

      // Call parent component's search handler with dates and price data
      onSearch({
        pickup: dates.pickup.toISOString(),
        dropoff: dates.dropoff.toISOString(),
        dynamicPriceMap: dynamicPriceMap
      });
    } catch (error) {
      console.error("Error fetching car availability:", error);
      
      // If there's an error, still perform the search but without the dynamic prices
      onSearch({
        pickup: dates.pickup.toISOString(),
        dropoff: dates.dropoff.toISOString()
      });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="bg-blue-900 rounded-2xl shadow-2xl p-6 mb-6 Aleo">
      <h2 className="text-white text-xl font-bold mb-4">Quick Search</h2>
      <div className="space-y-4 w-full">
        <div className='w-full'>
          <label className="block text-white text-sm mb-2">Pick-up Date</label>
          <DatePicker
            selected={dates.pickup}
            onChange={(date) => handleDateChange(date, 'pickup')}
            customInput={<CustomInput placeholder="Pick-up Date" isError={dateErrors.pickup} />}
            dateFormat="dd/MM/yyyy HH:mm"
            minDate={new Date()}
            showTimeSelect
            placeholderText="Pick-up Date"
            wrapperClassName='w-full'
            open={datePickerOpen.pickup}
            onClickOutside={() => setDatePickerOpen(prev => ({ ...prev, pickup: false }))}
            onInputClick={() => setDatePickerOpen(prev => ({ ...prev, pickup: true }))}
          />
          {dateErrors.pickup && (
            <motion.p 
              className="text-red-300 text-xs mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Invalid pick-up date
            </motion.p>
          )}
        </div>
        <div>
          <label className="block text-white text-sm mb-2">Drop-off Date</label>
          <DatePicker
            selected={dates.dropoff}
            onChange={(date) => handleDateChange(date, 'dropoff')}
            customInput={<CustomInput placeholder="Drop-off Date" isError={dateErrors.dropoff} />}
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeSelect
            minDate={dates.pickup || new Date()}
            placeholderText="Drop-off Date"
            wrapperClassName='w-full'
            open={datePickerOpen.dropoff}
            onClickOutside={() => setDatePickerOpen(prev => ({ ...prev, dropoff: false }))}
            onInputClick={() => setDatePickerOpen(prev => ({ ...prev, dropoff: true }))}
          />
          {dateErrors.dropoff && (
            <motion.p 
              className="text-red-300 text-xs mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Invalid drop-off date
            </motion.p>
          )}
        </div>        <motion.button
          onClick={handleSearch}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition duration-300"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isSearching}
        >
          {isSearching ? "Searching..." : "Search"}
        </motion.button>
      </div>
    </div>
  );
};

export default QuickSearch;